/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {NgModuleFactory} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {fromPromise} from 'rxjs/observable/fromPromise';
import {of } from 'rxjs/observable/of';
import {concatAll} from 'rxjs/operator/concatAll';
import {every} from 'rxjs/operator/every';
import * as l from 'rxjs/operator/last';
import {map} from 'rxjs/operator/map';
import {mergeAll} from 'rxjs/operator/mergeAll';

import {isObservable, isPromise} from '../private_import_core';
import {PRIMARY_OUTLET} from '../shared';
/**
 * @param {?} a
 * @param {?} b
 * @return {?}
 */
export function shallowEqualArrays(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;
  for (let /** @type {?} */ i = 0; i < a.length; ++i) {
    if (!shallowEqual(a[i], b[i])) return false;
  }
  return true;
}
/**
 * @param {?} a
 * @param {?} b
 * @return {?}
 */
export function shallowEqual(a: {[x: string]: any}, b: {[x: string]: any}): boolean {
  const /** @type {?} */ k1 = Object.keys(a);
  const /** @type {?} */ k2 = Object.keys(b);
  if (k1.length != k2.length) {
    return false;
  }
  let /** @type {?} */ key: string;
  for (let /** @type {?} */ i = 0; i < k1.length; i++) {
    key = k1[i];
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}
/**
 * @param {?} a
 * @return {?}
 */
export function flatten<T>(a: T[][]): T[] {
  const /** @type {?} */ target: T[] = [];
  for (let /** @type {?} */ i = 0; i < a.length; ++i) {
    for (let /** @type {?} */ j = 0; j < a[i].length; ++j) {
      target.push(a[i][j]);
    }
  }
  return target;
}
/**
 * @param {?} a
 * @return {?}
 */
export function first<T>(a: T[]): T {
  return a.length > 0 ? a[0] : null;
}
/**
 * @param {?} a
 * @return {?}
 */
export function last<T>(a: T[]): T {
  return a.length > 0 ? a[a.length - 1] : null;
}
/**
 * @param {?} bools
 * @return {?}
 */
export function and(bools: boolean[]): boolean {
  return !bools.some(v => !v);
}
/**
 * @param {?} m1
 * @param {?} m2
 * @return {?}
 */
export function merge<V>(m1: {[key: string]: V}, m2: {[key: string]: V}): {[key: string]: V} {
  const /** @type {?} */ m: {[key: string]: V} = {};

  for (const /** @type {?} */ attr in m1) {
    if (m1.hasOwnProperty(attr)) {
      m[attr] = m1[attr];
    }
  }

  for (const /** @type {?} */ attr in m2) {
    if (m2.hasOwnProperty(attr)) {
      m[attr] = m2[attr];
    }
  }

  return m;
}
/**
 * @param {?} map
 * @param {?} callback
 * @return {?}
 */
export function forEach<K, V>(map: {[key: string]: V}, callback: (v: V, k: string) => void): void {
  for (const /** @type {?} */ prop in map) {
    if (map.hasOwnProperty(prop)) {
      callback(map[prop], prop);
    }
  }
}
/**
 * @param {?} obj
 * @param {?} fn
 * @return {?}
 */
export function waitForMap<A, B>(
    obj: {[k: string]: A}, fn: (k: string, a: A) => Observable<B>): Observable<{[k: string]: B}> {
  const /** @type {?} */ waitFor: Observable<B>[] = [];
  const /** @type {?} */ res: {[k: string]: B} = {};

  forEach(obj, (a: A, k: string) => {
    if (k === PRIMARY_OUTLET) {
      waitFor.push(map.call(fn(k, a), (_: B) => {
        res[k] = _;
        return _;
      }));
    }
  });

  forEach(obj, (a: A, k: string) => {
    if (k !== PRIMARY_OUTLET) {
      waitFor.push(map.call(fn(k, a), (_: B) => {
        res[k] = _;
        return _;
      }));
    }
  });

  if (waitFor.length > 0) {
    const /** @type {?} */ concatted$ = concatAll.call(of (...waitFor));
    const /** @type {?} */ last$ = l.last.call(concatted$);
    return map.call(last$, () => res);
  }

  return of (res);
}
/**
 * @param {?} observables
 * @return {?}
 */
export function andObservables(observables: Observable<Observable<any>>): Observable<boolean> {
  const /** @type {?} */ merged$ = mergeAll.call(observables);
  return every.call(merged$, (result: any) => result === true);
}
/**
 * @param {?} value
 * @return {?}
 */
export function wrapIntoObservable<T>(value: T | NgModuleFactory<T>| Promise<T>| Observable<T>):
    Observable<T> {
  if (isObservable(value)) {
    return value;
  }

  if (isPromise(value)) {
    return fromPromise(value);
  }

  return of (value);
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
